/**
 * LOBBERO STAYS — a stay's recorded amounts in the browser (ai-plans/stays plan
 * S4, R-S4.9; K-48 / K-53). Nothing here is charged: these are figures the host
 * types for their own records.
 *
 * Money travels as INTEGER minor units (cents). The form reads what a person
 * typed with parseAmount() — digits with ONE optional "." and at most the
 * currency's minor digits; no thousands separators, no comma decimals, no
 * minus — and never rounds: "12.345" in euros is refused, not guessed.
 * formatMoney() prints in en-GB ("€420.00"). The cases are a COPY of
 * lobbero_be/scripts/stays/money-cases.json (stays-money.cases.json, kept by
 * check-fe-copy.js); the backend twin is lobbero_be/src/stays/money.js.
 */

/** ISO 4217 minor digits (the backend's CURRENCY_DIGITS). */
export const CURRENCY_DIGITS: Readonly<Record<string, number>> = Object.freeze({
  EUR: 2,
  GBP: 2,
  CHF: 2,
  SEK: 2,
  MKD: 2,
  USD: 2,
  CAD: 2,
  AED: 2,
  JPY: 0,
  KWD: 3,
});

export const digitsOf = (currency: string): number =>
  Object.prototype.hasOwnProperty.call(CURRENCY_DIGITS, currency) ? CURRENCY_DIGITS[currency] : 2;

/** 10,000,000.00 in any currency's minor units — the server's ceiling. */
export const MONEY_MAX_CENTS = 1000000000;

export type ParsedAmount = { cents: number | null; error: string | null };

/** "420.5" → 42050 (EUR). Empty → null (no amount). Anything else → an error sentence. */
export const parseAmount = (text: string, currency: string): ParsedAmount => {
  const raw = String(text ?? '').trim();
  if (raw === '') return { cents: null, error: null };
  const digits = digitsOf(currency);
  const pattern = digits === 0 ? /^\d+$/ : new RegExp(`^\\d+(\\.\\d{1,${digits}})?$`);
  if (!pattern.test(raw)) {
    return {
      cents: null,
      error:
        digits === 0
          ? `Enter a whole number in ${currency}, like 1200.`
          : `Enter an amount like 420.50, with at most ${digits} decimal place${digits === 1 ? '' : 's'}.`,
    };
  }
  const [whole, frac = ''] = raw.split('.');
  const cents = Number(whole) * 10 ** digits + Number((frac + '0'.repeat(digits)).slice(0, digits) || '0');
  if (!Number.isSafeInteger(cents) || cents > MONEY_MAX_CENTS) {
    return { cents: null, error: 'That amount is too large (at most 10,000,000).' };
  }

  return { cents, error: null };
};

/** 42050 → "420.50" (EUR): the text an input shows for a stored amount. */
export const centsToInput = (cents: number | null | undefined, currency: string): string => {
  if (cents == null) return '';
  const digits = digitsOf(currency);
  if (digits === 0) return String(cents);
  const s = String(Math.abs(cents)).padStart(digits + 1, '0');

  return `${s.slice(0, -digits)}.${s.slice(-digits)}`;
};

/** 42050 → "€420.50" (en-GB). Falls back to "420.50 EUR" for an unknown code. */
export const formatMoney = (cents: number | null | undefined, currency: string): string => {
  if (cents == null) return '';
  const digits = digitsOf(currency);
  const value = cents / 10 ** digits;
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(value);
  } catch {
    return `${centsToInput(cents, currency)} ${currency}`;
  }
};

/** A whole-number percentage of `part` in `total` ("15 %"), or null without a total. */
export const percentOf = (part: number | null | undefined, total: number | null | undefined): number | null => {
  if (part == null || total == null || total <= 0) return null;

  return Math.round((part / total) * 100);
};

/** gross − fee − tax, never below 0 (the calculated payout, for the live preview). */
export const calculatedPayout = (
  gross: number | null,
  fee: number | null,
  tax: number | null
): number | null => (gross == null ? null : Math.max(0, gross - (fee || 0) - (tax || 0)));
